/**
 * @xait/charts-angularjs — XaitCharts for AngularJS 1.5+.
 *
 * Registers the `xait.charts` module with the chart components
 * (`<xa-stacked-bar>`, `<xa-stacked-bar-h>`, `<xa-donut>`, `<xa-pie>`,
 * `<xa-line>`, `<xa-area>`), the internal `<xac-legend>`, and the
 * `xaChartsConfig` provider (hosts see it as `xaChartsConfigProvider` in
 * `.config()` blocks). All layout math, formatting, palettes and CSS come
 * from @xait/charts-core — the wrappers only hold interaction state and
 * render the core view-models.
 *
 * Usage:
 * ```js
 * angular.module('app', ['xait.charts']);
 * ```
 * ```html
 * <xa-stacked-bar data="$ctrl.usage" legend="'top'"
 *                 on-series-toggle="$ctrl.onToggle($event)"></xa-stacked-bar>
 * ```
 */
import angular from 'angular';
import { ensureChartStyles } from '@xait/charts-core';
import { XaChartsConfigProvider } from './config';
import { xacLegendComponent } from './legend.component';
import { xaStackedBarComponent } from './stacked-bar.component';
import { xaStackedBarHComponent } from './stacked-bar-h.component';
import { xaDonutComponent } from './donut.component';
import { xaPieComponent } from './pie.component';
import { xaLineComponent } from './line.component';
import { xaAreaComponent } from './area.component';

/** AngularJS module name to depend on: `angular.module('app', [XAIT_CHARTS_MODULE])`. */
export const XAIT_CHARTS_MODULE = 'xait.charts';

angular
  .module(XAIT_CHARTS_MODULE, [])
  .provider('xaChartsConfig', XaChartsConfigProvider)
  .component('xacLegend', xacLegendComponent)
  .component('xaStackedBar', xaStackedBarComponent)
  .component('xaStackedBarH', xaStackedBarHComponent)
  .component('xaDonut', xaDonutComponent)
  .component('xaPie', xaPieComponent)
  .component('xaLine', xaLineComponent)
  .component('xaArea', xaAreaComponent)
  .run([
    '$document',
    function ($document: ng.IDocumentService) {
      // Inject the shared chart CSS once (id-guarded in the core).
      ensureChartStyles($document[0] as unknown as Document);
    },
  ]);

export { XaChartsConfigProvider } from './config';
export type { XaChartsDefaults } from './config';

/* Re-export the public data/config/event types for typed hosts. */
export type {
  XaCategorical,
  XaSeries,
  XaSlice,
  XaPalette,
  XaLegendPos,
  XaLegendLayout,
  XaSeriesToggleEvent,
  XaHoverEvent,
  XaSliceHoverEvent,
  XaSliceClickEvent,
} from '@xait/charts-core';
